import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { QuoteComparisonDto } from "@procureflow/shared-types";

// US-021 — Quote comparison surface (AUTO-Q3: dedicated route
// `/rfq/$id/compare`). Renders a matrix of rfq_line_items × vendors,
// flags is_best_price cells with `data-best-price="true"` for both the
// spec assertion and visual styling (green + ★). Award buttons appear
// per vendor as disabled placeholders; Plan 04-07 wires the onClick.
//
// BE/MSW @Roles is `ProcurementOfficer, Admin`; Finance + Requester
// see the forbidden message — guarded both client-side and server-side.

export const Route = createFileRoute("/rfq/$id/compare")({
  head: () => ({
    meta: [{ title: "Quote comparison — ProcureFlow" }],
  }),
  component: CompareQuotesPage,
});

function CompareQuotesPage() {
  const { id: rfqId } = useParams({ from: "/rfq/$id/compare" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = user?.role === "ProcurementOfficer" || user?.role === "Admin";
  const canAward = user?.role === "ProcurementOfficer";

  const [comparison, setComparison] = useState<QuoteComparisonDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // US-022: award confirmation Dialog state. Track which quote (by id)
  // currently has its Dialog open + the in-flight POST flag so the
  // Cancel button can be disabled while submitting.
  const [awardingQuoteId, setAwardingQuoteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleAward(quoteId: string) {
    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/quotes/${quoteId}/award`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.message ?? "Failed to award quote.");
        setAwardingQuoteId(null);
        return;
      }
      const okJson = (await res.json()) as {
        data: {
          purchase_order: { id: string; po_number: string };
        };
      };
      toast.success(`PO ${okJson.data.purchase_order.po_number} created`);
      navigate({
        to: "/purchase-orders/$id",
        params: { id: okJson.data.purchase_order.id },
      });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      toast.error("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const res = await fetchWithAuth(`/api/rfqs/${rfqId}/quotes/compare`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setError(body?.message ?? "Failed to load comparison.");
            setLoading(false);
          }
          return;
        }
        const json = (await res.json()) as { data: QuoteComparisonDto };
        if (!cancelled) {
          setComparison(json.data);
          setLoading(false);
        }
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) {
          setError("An unexpected error occurred.");
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [rfqId, allowed]);

  if (!allowed) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-muted-foreground">You do not have access to RFQs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq/$id"
          params={{ id: rfqId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to RFQ
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-destructive">{error ?? "No data."}</p>
        </div>
      </div>
    );
  }

  const { rfq_line_items, vendors } = comparison;
  const hasVendors = vendors.length > 0;

  return (
    <div className="space-y-5">
      <Link
        to="/rfq/$id"
        params={{ id: rfqId }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to RFQ
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6">
        <h1 className="text-lg font-semibold">Quote comparison</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Side-by-side line pricing for every submitted quote. Best price on each line is
          highlighted in green ★.
        </p>
      </div>

      {!hasVendors ? (
        <div
          data-testid="empty-state"
          className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center"
        >
          <p className="text-sm text-muted-foreground">No quotes recorded yet for this RFQ.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-72">Line item</TableHead>
                <TableHead className="w-20">Qty</TableHead>
                {vendors.map((v) => (
                  <TableHead key={v.vendor_id} data-testid={`vendor-col-${v.vendor_id}`}>
                    <div className="flex flex-col gap-1.5">
                      <span className="font-medium">{v.company_name}</span>
                      <Badge variant="secondary">{v.status}</Badge>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rfq_line_items.map((li) => (
                <TableRow key={li.id}>
                  <TableCell>{li.description}</TableCell>
                  <TableCell className="tabular-nums">{li.quantity}</TableCell>
                  {vendors.map((v) => {
                    const lp = v.line_prices.find((p) => p.rfq_line_item_id === li.id);
                    if (!lp) {
                      return (
                        <TableCell
                          key={v.vendor_id}
                          data-best-price="false"
                          className="text-muted-foreground"
                        >
                          —
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={v.vendor_id}
                        data-best-price={String(lp.is_best_price)}
                        className={lp.is_best_price ? "font-semibold text-green-700" : ""}
                      >
                        {lp.is_best_price && (
                          <span aria-label="best price" className="mr-1">
                            ★
                          </span>
                        )}
                        <span className="tabular-nums">{lp.unit_price}</span>
                        <span className="text-muted-foreground text-xs ml-2">
                          (line: {lp.line_total})
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} className="font-medium">
                  Total
                </TableCell>
                {vendors.map((v) => (
                  <TableCell
                    key={v.vendor_id}
                    data-testid={`vendor-total-${v.vendor_id}`}
                    className="font-semibold tabular-nums"
                  >
                    {v.total_amount}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} className="font-medium">
                  Award
                </TableCell>
                {vendors.map((v) => (
                  <TableCell key={v.vendor_id}>
                    {/* US-022: Award button visible only for Submitted
                        quotes to ProcurementOfficer (BE @Roles parity —
                        Admin is NOT permitted on the award endpoint per
                        M3.B-shipped quotes.controller.ts). Clicking opens
                        a confirmation Dialog; Confirm POSTs and navigates
                        to the resulting PO; toast surfaces success or any
                        400 envelope (e.g. INVALID_RFQ_STATE if a sibling
                        was awarded concurrently). */}
                    {v.status === "Submitted" && canAward && (
                      <Dialog
                        open={awardingQuoteId === v.quote_id}
                        onOpenChange={(open) => {
                          if (!open) setAwardingQuoteId(null);
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`award-${v.vendor_id}`}
                            onClick={() => setAwardingQuoteId(v.quote_id)}
                          >
                            Award
                          </Button>
                        </DialogTrigger>
                        <DialogContent dismissable={false}>
                          <DialogHeader>
                            <DialogTitle>Award this quote?</DialogTitle>
                            <DialogDescription>
                              Award the quote from <strong>{v.company_name}</strong>. This will mark
                              every other quote on this RFQ as Declined, close the RFQ, and generate
                              a Purchase Order. You won't be able to undo this.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setAwardingQuoteId(null)}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button onClick={() => handleAward(v.quote_id)} disabled={submitting}>
                              {submitting ? "Awarding…" : "Confirm award"}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
